import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth, anonymize } from "../App";
import { GakEak } from "../utils/caseFormat";
import { Download, FileText, Calendar, Clock, Building2, Filter, Loader2, CheckCircle, XCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { BranchFilterSelect } from "../components/Workspace";
import { readWorkspace } from "../utils/workspace";
import { DateInput } from "../components/ui/date-input";

const Reports = () => {
  const { user } = useAuth();
  const privacy = user?.privacy_mode || false;
  const p = (text) => privacy ? anonymize(text) : text;
  const userCourtTypes = user?.court_types || ["Μονομελές", "Πολυμελές"];
  const [casesNoHearing, setCasesNoHearing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courtFilter, setCourtFilter] = useState(() => userCourtTypes.length === 1 ? userCourtTypes[0] : (readWorkspace(user)?.court_type || ""));
  const [branchFilter, setBranchFilter] = useState(() => readWorkspace(user)?.branch || "");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [exportDays, setExportDays] = useState(30);
  const [filingDateFrom, setFilingDateFrom] = useState("");
  const [filingDateTo, setFilingDateTo] = useState("");
  const [activeExports, setActiveExports] = useState({});

  useEffect(() => { fetchSubjects(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCasesNoHearing(); }, [courtFilter, subjectFilter, branchFilter]);

  const fetchSubjects = async () => {
    try { const r = await axios.get(`${API}/cases/subjects`); setSubjects(r.data); }
    catch (error) { console.error("Reports: failed to fetch subjects", error); }
  };

  const fetchCasesNoHearing = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (courtFilter && courtFilter !== "all") params.append("court_type", courtFilter);
      if (branchFilter) params.append("branch", branchFilter);
      if (subjectFilter && subjectFilter !== "all") params.append("subject", subjectFilter);
      const response = await axios.get(`${API}/cases/no-hearing?${params.toString()}`);
      setCasesNoHearing(response.data.cases || []);
    } catch { toast.error("Σφάλμα φόρτωσης υποθέσεων"); }
    finally { setLoading(false); }
  };

  const pollExport = (taskId, exportKey, filename) => {
    setActiveExports(prev => ({ ...prev, [exportKey]: { taskId, status: "running" } }));

    const poll = setInterval(async () => {
      try {
        const status = await axios.get(`${API}/export/status/${taskId}`);
        if (status.data.status === "completed") {
          clearInterval(poll);
          setActiveExports(prev => ({ ...prev, [exportKey]: { ...prev[exportKey], status: "completed" } }));
          const dl = await axios.get(`${API}/export/download/${taskId}`, { responseType: 'blob' });
          const downloadUrl = window.URL.createObjectURL(new Blob([dl.data]));
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(downloadUrl);
          toast.success(`Εξαγωγή ${status.data.count} υποθέσεων ολοκληρώθηκε!`);
          setTimeout(() => setActiveExports(prev => { const n = {...prev}; delete n[exportKey]; return n; }), 3000);
        } else if (status.data.status === "error") {
          clearInterval(poll);
          setActiveExports(prev => ({ ...prev, [exportKey]: { ...prev[exportKey], status: "error", error: status.data.error } }));
          toast.error("Σφάλμα εξαγωγής: " + (status.data.error || ""));
          setTimeout(() => setActiveExports(prev => { const n = {...prev}; delete n[exportKey]; return n; }), 5000);
        }
      } catch (error) {
        console.error("Reports: export poll error", error);
      }
    }, 2000);
    setTimeout(() => clearInterval(poll), 300000);
  };

  const buildFilterParams = () => {
    const parts = [];
    if (courtFilter && courtFilter !== "all") parts.push(`court_type=${encodeURIComponent(courtFilter)}`);
    if (branchFilter) parts.push(`branch=${encodeURIComponent(branchFilter)}`);
    if (filingDateFrom) parts.push(`filing_date_from=${filingDateFrom}`);
    if (filingDateTo) parts.push(`filing_date_to=${filingDateTo}`);
    return parts.join("&");
  };

  const handleExportCases = async (noHearingOnly = false, exportFormat = "xlsx") => {
    try {
      const key = `cases_${exportFormat}${noHearingOnly ? '_nh' : ''}`;
      toast.info("Δημιουργία αρχείου εξαγωγής...");
      let url = `${API}/export/cases?format=${exportFormat}`;
      const filters = buildFilterParams();
      if (filters) url += `&${filters}`;
      if (noHearingOnly) url += `&no_hearing=true`;
      const response = await axios.get(url);
      const ext = exportFormat === "xlsx" ? "xlsx" : "csv";
      const dateStr = new Date().toISOString().split('T')[0];
      pollExport(response.data.task_id, key, `cases_export_${dateStr}.${ext}`);
    } catch (e) {
      toast.error("Σφάλμα εξαγωγής");
    }
  };

  const handleExportCasesZip = async () => {
    try {
      toast.info("Δημιουργία ZIP αρχείου (1000 υποθέσεις/φύλλο)...");
      let url = `${API}/export/cases-zip`;
      const filters = buildFilterParams();
      if (filters) url += `?${filters}`;
      const response = await axios.get(url);
      const dateStr = new Date().toISOString().split('T')[0];
      pollExport(response.data.task_id, "zip", `cases_export_${dateStr}.zip`);
    } catch (e) {
      toast.error("Σφάλμα εξαγωγής ZIP");
    }
  };

  const handleExportDeadlines = async (exportFormat = "xlsx") => {
    try {
      let url = `${API}/export/deadlines?days=${exportDays}&format=${exportFormat}`;
      if (courtFilter && courtFilter !== "all") url += `&court_type=${encodeURIComponent(courtFilter)}`;
      if (branchFilter) url += `&branch=${encodeURIComponent(branchFilter)}`;
      const response = await axios.get(url, { responseType: 'blob' });
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      const ext = exportFormat === "xlsx" ? "xlsx" : "csv";
      link.setAttribute('download', `deadlines_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Το αρχείο εξήχθη επιτυχώς");
    } catch { toast.error("Σφάλμα εξαγωγής"); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("el-GR");
  };

  const ExportStatus = ({ exportKey }) => {
    const exp = activeExports[exportKey];
    if (!exp) return null;
    return (
      <div className="flex items-center gap-1.5 mt-2">
        {exp.status === "running" && <><Loader2 className="w-3 h-3 animate-spin text-blue-500" /><span className="text-xs text-blue-600">Εξαγωγή σε εξέλιξη...</span></>}
        {exp.status === "completed" && <><CheckCircle className="w-3 h-3 text-emerald-500" /><span className="text-xs text-emerald-600">Ολοκληρώθηκε!</span></>}
        {exp.status === "error" && <><XCircle className="w-3 h-3 text-red-500" /><span className="text-xs text-red-600">Σφάλμα</span></>}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Αναφορές & Εξαγωγές</h1>
        <p className="text-slate-500 mt-1">Εξαγωγή δεδομένων και αναφορών</p>
      </div>

      {/* Global Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            Φίλτρα Εξαγωγής
          </CardTitle>
          <CardDescription>Εφαρμόζονται σε όλες τις εξαγωγές υποθέσεων</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Κατάστημα</Label>
              <BranchFilterSelect value={branchFilter} onChange={setBranchFilter} className="w-60" testId="branch-filter-reports" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Τύπος Δικαστηρίου</Label>
              <Select value={courtFilter} onValueChange={setCourtFilter}
                disabled={user?.role !== "admin" && userCourtTypes.length === 1}>
                <SelectTrigger className="w-48" data-testid="court-filter-reports">
                  <SelectValue placeholder="Όλοι οι τύποι" />
                </SelectTrigger>
                <SelectContent>
                  {(user?.role === "admin" || userCourtTypes.length > 1) && (
                    <SelectItem value="all">Όλοι οι τύποι</SelectItem>
                  )}
                  {(user?.role === "admin" ? ["Μονομελές", "Πολυμελές"] : userCourtTypes).map(ct => (
                    <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Αντικείμενο</Label>
              <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                <SelectTrigger className="w-52" data-testid="subject-filter-reports">
                  <SelectValue placeholder="Όλα τα αντικείμενα" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Όλα τα αντικείμενα</SelectItem>
                  {subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Κατάθεση Από</Label>
              <DateInput value={filingDateFrom} onChange={e => setFilingDateFrom(e.target.value)}
                className="w-40" data-testid="filing-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Κατάθεση Έως</Label>
              <DateInput value={filingDateTo} onChange={e => setFilingDateTo(e.target.value)}
                className="w-40" data-testid="filing-date-to" />
            </div>
            {(courtFilter || filingDateFrom || filingDateTo || subjectFilter) && (
              <Button variant="ghost" size="sm" className="text-slate-500"
                onClick={() => { setCourtFilter(""); setBranchFilter(""); setFilingDateFrom(""); setFilingDateTo(""); setSubjectFilter(""); }}
                data-testid="clear-filters-btn">
                Καθαρισμός
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Export Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-hover">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">Εξαγωγή Υποθέσεων</CardTitle>
                <CardDescription>Με φίλτρα</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button onClick={() => handleExportCases(false, "xlsx")} className="flex-1"
                disabled={!!activeExports.cases_xlsx}
                data-testid="export-all-cases-btn">
                {activeExports.cases_xlsx?.status === "running" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Excel
              </Button>
              <Button onClick={handleExportCasesZip} variant="outline" size="sm"
                disabled={!!activeExports.zip}
                data-testid="export-all-cases-zip-btn"
                title="ZIP αρχείο με 1000 υποθέσεις ανά φύλλο">
                {activeExports.zip?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : "ZIP"}
              </Button>
              <Button onClick={() => handleExportCases(false, "csv")} variant="outline" size="sm"
                disabled={!!activeExports.cases_csv}
                data-testid="export-all-cases-csv-btn">
                CSV
              </Button>
            </div>
            <ExportStatus exportKey="cases_xlsx" />
            <ExportStatus exportKey="zip" />
            <ExportStatus exportKey="cases_csv" />
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-base">Χωρίς Δικάσιμο</CardTitle>
                <CardDescription>{casesNoHearing.length} υποθέσεις</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button onClick={() => handleExportCases(true, "xlsx")} className="flex-1" variant="outline"
                disabled={!!activeExports.cases_xlsx_nh}
                data-testid="export-no-hearing-btn">
                {activeExports.cases_xlsx_nh?.status === "running" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Excel
              </Button>
              <Button onClick={() => handleExportCases(true, "csv")} variant="outline" size="sm"
                disabled={!!activeExports.cases_csv_nh}
                data-testid="export-no-hearing-csv-btn">
                CSV
              </Button>
            </div>
            <ExportStatus exportKey="cases_xlsx_nh" />
            <ExportStatus exportKey="cases_csv_nh" />
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-base">Προθεσμίες</CardTitle>
                <CardDescription>Επόμενες {exportDays} ημέρες</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <Input type="number" min="7" max="365" value={exportDays}
              onChange={(e) => setExportDays(parseInt(e.target.value) || 30)}
              className="mb-2" data-testid="export-days-input" />
            <Button onClick={() => handleExportDeadlines("xlsx")} className="w-full" variant="outline" data-testid="export-deadlines-btn">
              <Download className="w-4 h-4 mr-2" />Excel
            </Button>
            <Button onClick={() => handleExportDeadlines("csv")} variant="outline" size="sm" className="w-full" data-testid="export-deadlines-csv-btn">
              <Download className="w-4 h-4 mr-2" />CSV
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Cases Without Hearing */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-500" />
                Υποθέσεις Χωρίς Δικάσιμο
              </CardTitle>
              <CardDescription>Υποθέσεις χωρίς ημερομηνία δικασίμου</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : casesNoHearing.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Όλες οι υποθέσεις έχουν ορισθεί δικάσιμο</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {casesNoHearing.map(caseItem => (
                <div key={caseItem.id} className="p-4 rounded-lg bg-amber-50 border border-amber-200"
                  data-testid={`no-hearing-case-${caseItem.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-slate-900">
                          <GakEak caseObj={caseItem} />
                        </span>
                        <Badge className={caseItem.court_type === "Μονομελές" ? "court-mono" : "court-poly"}>
                          {caseItem.court_type}
                        </Badge>
                        {caseItem.subject && (
                          <Badge variant="outline" className="text-xs">{caseItem.subject}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">Κατάθεση: {formatDate(caseItem.filing_date)}</p>
                      {caseItem.plaintiffs && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate max-w-md">{p(caseItem.plaintiffs)}</p>
                      )}
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 border border-amber-200">Χωρίς Δικάσιμο</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
